export interface ChildWithType {
  type: string;
  children: ChildWithType[] | undefined | null;
}

function childIsOpen(child: ChildWithType) {
  return child.type === 'extended_cms_group_open';
}

function childIsClosed(child: ChildWithType) {
  return child.type === 'extended_cms_group_close';
}

const EXTENDED_CMS_GROUP = 'extended_cms_group';

function createExtendedGroupsForChildren(
  children: ChildWithType[] | null | undefined
): ChildWithType[] | null {
  if (!children) {
    return null;
  }

  const newChildren: ChildWithType[] = [];
  let openGroupCount = 0;
  let extendedGroup: ChildWithType | null = null;

  for (let i = 0; i < children.length; i++) {
    const child = children[i];

    if (childIsOpen(child)) {
      openGroupCount++;
      // Create an extended group and put children until there are no open groups
      if (!extendedGroup) {
        // It's important to notice that this creates a group which is not "open".
        // This is used for the case where an open group might have children (only in test cases, but who knows?)
        extendedGroup = { ...child, type: EXTENDED_CMS_GROUP };
      } else {
        if (!extendedGroup.children) {
          extendedGroup.children = [];
        }
        // if we have an extended group active, that means we are currently creating a open/close grouping
        extendedGroup.children.push(child);
      }
    } else if (childIsClosed(child)) {
      openGroupCount--;
      // if we have closed the active grouping, add it to the output
      if (openGroupCount === 0) {
        if (extendedGroup) {
          // recurse for the children we have added to the group
          extendedGroup.children = createExtendedGroupsForChildren(
            extendedGroup.children
          );
          newChildren.push(extendedGroup);
          extendedGroup = null;
        } else {
          // eslint-disable-next-line no-console
          console.error(
            'Extended group should exist, if we have just set open groups to 0.'
          );
        }
      } else if (openGroupCount > 0) {
        if (extendedGroup) {
          if (extendedGroup.children) {
            extendedGroup.children.push(child);
          } else {
            // eslint-disable-next-line no-console
            console.error('Children on extended group _should_ exist.');
          }
        } else {
          // eslint-disable-next-line no-console
          console.error(
            'Extended group should exist, if there are open groups.'
          );
        }
      } else if (openGroupCount < 0) {
        // eslint-disable-next-line no-console
        console.error(
          'We should never end in this case. (more closing groups than openings)'
        );
      }
    } else if (openGroupCount > 0) {
      if (extendedGroup) {
        if (!extendedGroup.children) {
          extendedGroup.children = [];
        }
        extendedGroup.children.push(child);
      } else {
        // eslint-disable-next-line no-console
        console.error('Extended group should exist, if there are open groups.');
      }
    } else {
      // No open groups - it's just a regular child. Push it - but handle its children as well.
      if (child.children) {
        child.children = createExtendedGroupsForChildren(child.children);
      }
      newChildren.push(child);
    }

    if (openGroupCount < 0) {
      // eslint-disable-next-line no-console
      console.error('There was too many closing groups.');
    }
  }

  if (openGroupCount > 0) {
    // eslint-disable-next-line no-console
    console.warn(
      'Ill formed grouping. There was ' +
        openGroupCount +
        ' open groups. Closing them.'
    );
    if (extendedGroup) {
      extendedGroup.children = createExtendedGroupsForChildren(
        extendedGroup.children
      );
      newChildren.push(extendedGroup);
    } else {
      // eslint-disable-next-line no-console
      console.error('Expected an extended group to be present.');
    }
  }

  return newChildren;
}

export function hoistExtendedOpenClosedGroups(elements: ChildWithType[]) {
  return createExtendedGroupsForChildren(elements);
}
